import { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { heardFromText } from './heardFromText';
import { track } from '@/lib/analytics';
import { useOnboardingActive } from '@/lib/onboarding';

/****************
 * "HOW DID YOU HEAR ABOUT COPYFI?" 2026-10-21 (Mishka).
 *
 * The invisible half of source tracking. Links can be cleaned of every label,
 * but an in-app browser (Instagram, TikTok) drops the cookie the moment the
 * person finishes signing up in another browser, so some accounts arrive with
 * no source at all. Asking is the one measure that never depends on a click
 * path. One tap, once, for an account created in the last week; the answer is
 * a tracking-plan event (target = the choice), so it sits beside the account's
 * other activity and needs no table of its own.
 *
 * The "answered" flag is the browser's, per account, and deliberately NOT
 * forgotten at sign-out: it holds no personal data, and forgetting it would
 * ask the same person again.
 ****************/
export const HEARD_FROM_CHOICES = ['instagram', 'tiktok', 'whatsapp', 'telegram', 'email', 'youtube', 'x', 'friend', 'search', 'other'] as const;
const NEW_ACCOUNT_MS = 7 * 24 * 3600_000;

export const heardFromFlag = (userId: string) => `copyfi.heardFrom:${userId}`;

/** A new account (under a week old) that has not answered or skipped yet. */
export function shouldAskHeardFrom(createdAt: Date | null | undefined, answered: boolean, now = Date.now()): boolean {
  if (answered || !createdAt) return false;
  const age = now - createdAt.getTime();
  return age >= 0 && age <= NEW_ACCOUNT_MS;
}

function wasAnswered(userId: string): boolean {
  try { return !!localStorage.getItem(heardFromFlag(userId)); } catch { return false; }
}

export function HeardFromCard() {
  const { i18n } = useTranslation('dashboard');
  const t = heardFromText(i18n.language);
  const { user, isLoaded } = useUser();
  const [done, setDone] = useState<'thanks' | 'gone' | null>(null);
  // Not while the welcome chooser, its video or a guide is on screen: it waits its turn.
  const welcoming = useOnboardingActive();

  if (!isLoaded || !user || done === 'gone' || welcoming) return null;
  if (done === null && !shouldAskHeardFrom(user.createdAt, wasAnswered(user.id))) return null;

  const answer = (choice: string) => {
    track('heard_from_answered', choice);
    try { localStorage.setItem(heardFromFlag(user.id), choice); } catch { /* asked again next visit: harmless */ }
    if (choice === 'skipped') { setDone('gone'); return; }
    setDone('thanks');
    window.setTimeout(() => setDone('gone'), 2500);
  };

  if (done === 'thanks') {
    return <div className="mb-3 px-4 py-3 rounded-xl border bg-muted/30 text-sm" role="status">{t.thanks}</div>;
  }

  return (
    <div className="mb-3 px-4 py-4 rounded-xl border bg-card" data-testid="heard-from">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-medium">{t.title}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t.subtitle}</p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => answer('skipped')}>{t.skip}</Button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {HEARD_FROM_CHOICES.map((c) => (
          <Button key={c} size="sm" variant="outline" onClick={() => answer(c)}>{t[c]}</Button>
        ))}
      </div>
    </div>
  );
}
